import React, { useEffect, useState } from 'react';
import SparePartsKeys_ProductListItem from './SparePartsKeys_ProductListItem.jsx';
import {
  canBuyServicePartRepair,
  fetchDataForAddToCartButtonInBatches,
} from '/Foundation/Common/code/Utils/Extranet/Utils';
import { getUnavailabilityMessage } from '/Foundation/Common/code/Views/Foundation/Common/productAvailability';
import Spinner from '/Foundation/React/code/Components/Extranet/Spinner';
import SparePartAddToCartButton from './SparePartAddToCartButton.jsx';
import { addCartLines } from '/Foundation/Common/code/Utils/Extranet/Utils';
import { LOCAL_STORAGE, ROLE_GUIDS } from '/Foundation/React/code/Utilities/ExtranetConstants';
import { safeGetItem } from '/Foundation/React/code/Utilities/LocalStorage';
import { AdobeAnalyticsUtil } from '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/AdobeAnalyticsUtil';
import { Analytics_AddToCart } from '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/Analytics_AddToCart';
import { updateCart } from '/Foundation/Common/code/Views/Foundation/Common/AddToCartButton.js';
import CommerceApiFetchWrapper from '/Foundation/Common/code/Utils/CommerceApiFetchWrapper';

const SparePartsKeys_ProductList = ({ spareParts, translations, servicePartsModalLink, isKeysAndLocks }) => {
  const [prices, setPrices] = useState(null);
  const [inventoryMap, setInventoryMap] = useState({});
  const isExtranet = _THULEDATA.SitecoreSiteName === 'Extranet';
  const user = isExtranet ? safeGetItem(LOCAL_STORAGE.EXTRANET_USER) : null;
  const [showPrices, setShowPrices] = useState(
    !isExtranet ||
      (localStorage.getItem('showPrices') === 'true' && user?.HighestRole?.Id !== ROLE_GUIDS.DEALER_HIDE_PRICES)
  );

  const extranetHandler = (() => {
    return {
      getExtranetPrices: () => {
        if (!spareParts || !isExtranet) return;

        setPrices(null);
        let availableProducts = [];
        spareParts.forEach((sparePart) => {
          availableProducts.push({
            itemNumber: sparePart.Id,
            productType: 'Spare part',
            quantity: 1,
            isServicePartRepair: sparePart.IsServicePartRepair,
          });
        });

        fetchDataForAddToCartButtonInBatches(
          availableProducts,
          (data) => {
            setPrices(data);
          },
          (error) => {}
        );
      },
      handleTogglePrices: (show) => {
        setShowPrices(show);
      },
      addToCartExtranet: (id, quantity) => {
        let cartIdentifier = localStorage.getItem(LOCAL_STORAGE.CART_IDENTIFIER);
        const product = { cartId: cartIdentifier, productId: id, quantity: quantity };
        addCartLines(
          [product],
          (data) => {},
          (error) => {
            console.log(error);
          }
        );
      },
      getDeliverydate: (sparePart) => {
        if (isExtranet) return prices ? getItemresponseData(sparePart.Id)?.DeliveryDate : <div></div>;
        return null;
      },
    };
  })();

  useEffect(() => {
    if (isExtranet) extranetHandler.getExtranetPrices();
  }, [spareParts]);

  useEffect(() => {
    if (isExtranet) {
      extranetHandler.getExtranetPrices();
      window.addEventListener('toggleShowPrices', (e) => extranetHandler.handleTogglePrices(e.detail));
      return () => {
        window.removeEventListener('toggleShowPrices', extranetHandler.handleTogglePrices);
      };
    }
  }, []);

  const addToCart = async (sparePart, quantity, button, itemResponse) => {
    if (isExtranet) {
      extranetHandler.addToCartExtranet(sparePart.ProductId, quantity);
      const trackingData = Analytics_AddToCart?.GetTrackingDataForExtranetSparePart(
        sparePart,
        itemResponse,
        isKeysAndLocks
      );
      AdobeAnalyticsUtil.DataLayer.TrackEvent('cart_addition', trackingData);
      return;
    }
    if (!sparePart.ProductId) return;
    const productId = sparePart.ErpOutdoor ?? sparePart.Id ?? sparePart.ProductId;
    const products = [productId];
    await updateCart(products, button);
    const trackingData = Analytics_AddToCart?.GetTrackingDataFromSparePartObject(sparePart);
    if (trackingData) {
      AdobeAnalyticsUtil.DataLayer.TrackEvent('cart_addition', {
        add_to_cart_variant: trackingData.source,
        products: trackingData.products,
      });
    }
  };

  const getItemresponseData = (id) => {
    return prices?.ItemResponses?.find((x) => x.ItemNumber === id);
  };

  const getPriceElement = (sparePart) => {
    if (!showPrices) return null;
    if (isExtranet) {
      if (!prices)
        return (
          <div>
            <Spinner /> / {sparePart?.ProductData?.RRPFormatted}
          </div>
        );
      const itemResponse = getItemresponseData(sparePart.Id);
      return (
        <>
          {itemResponse?.SalesPrice} / {sparePart?.ProductData?.RRPFormatted}
        </>
      );
    }
    return sparePart?.PriceFormatted;
  };

  useEffect(() => {
    if (!window._THULEDATA?.isB2CEnabled) return;

    const payload = (spareParts || [])
      .map((sp) => {
        const productKey = sp?.ErpOutdoor ?? sp.Id ?? sp.ProductId;
        return {
          productId: sp.ProductId,
          skuIds: [productKey],
        };
      })
      .filter((p) => p.productId && p.skuIds.length > 0);

    if (payload.length === 0) return;

    CommerceApiFetchWrapper('/cart/inventory-batch', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then((response) => {
        const map = {};
        (response.items || []).forEach((item) => {
          if (item?.productId) map[item.productId] = item;
        });
        setInventoryMap(map);
      })
      .catch(() => setInventoryMap({}));
  }, [spareParts]);

  const getAddToCartButton = (sparePart) => {
    if (sparePart.MissingPfasReplacement) return <div>{sparePart.PfasErrorMessage}</div>;
    // Service Part Repair parts are only buyable by service-repair centers.
    if (isExtranet && !canBuyServicePartRepair(sparePart.IsServicePartRepair)) return null;
    return (
      <SparePartAddToCartButton
        servicePartsModalLink={servicePartsModalLink}
        translations={translations}
        itemResponseData={getItemresponseData(sparePart.Id)}
        prices={prices}
        addToCart={addToCart}
        sparePart={sparePart}
        isKeysAndLocks={isKeysAndLocks}
        inventory={inventoryMap[sparePart.ProductId]}
      />
    );
  };

  return (
    <div className="spm__productList" data-component-label="Spare part product listing">
      {spareParts && spareParts.length > 0 && (
        <table className="d-none d-lg-table">
          <thead>
            <tr>
              <th>{translations.Product}</th>
              <th>{translations.ArticleNumber}</th>
              <th>{isExtranet ? `${translations.Price}/${translations.RRP}` : translations.Price}</th>
              {isExtranet && <th>{translations.Availability}</th>}
              <th>
                <div className="sr-only">Action</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {spareParts.map((sparePart, index) => {
              const respData = getItemresponseData(sparePart.Id);
              const repairBlocked = isExtranet && !canBuyServicePartRepair(sparePart.IsServicePartRepair);
              const helperMessage = isExtranet
                ? getUnavailabilityMessage(
                    {
                      statusErp: sparePart.StatusERP,
                      hasAccess: sparePart.HasAccess,
                      isAvailable: respData?.IsAvailable,
                    },
                    translations
                  )
                : null;
              const backendMessage = isExtranet ? respData?.TranslatedErrorMessage : null;
              const unavailabilityMessage = repairBlocked
                ? translations?.NotInAssortment
                : (helperMessage ?? (backendMessage || null));
              return (
                <tr key={index}>
                  <td className="heading6">
                    <a target="_blank" href={sparePart.Url} rel="noreferrer">
                      {!isKeysAndLocks && sparePart.Badge && <span>{sparePart.Badge}. </span>}
                      <span className="productName">{sparePart.Name}</span>
                    </a>
                  </td>
                  <td className="paragraph--s">{sparePart.Id}</td>

                  {unavailabilityMessage ? (
                    <td>{unavailabilityMessage}</td>
                  ) : (
                    <>
                      <td className="paragraph--s">{getPriceElement(sparePart)}</td>
                      {isExtranet && (
                        <td className="paragraph--s">
                          <span className="me-4">{respData?.Availability}</span>
                          {extranetHandler.getDeliverydate(sparePart)}
                        </td>
                      )}
                      <td>{getAddToCartButton(sparePart)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {spareParts && spareParts.length > 0 && (
        <div className="d-lg-none">
          {spareParts.map((sparePart, index) => {
            const respData = getItemresponseData(sparePart.Id);
            const repairBlocked = isExtranet && !canBuyServicePartRepair(sparePart.IsServicePartRepair);
            const helperMessage = isExtranet
              ? getUnavailabilityMessage(
                  {
                    statusErp: sparePart.StatusERP,
                    hasAccess: sparePart.HasAccess,
                    isAvailable: respData?.IsAvailable,
                  },
                  translations
                )
              : null;
            const fallbackMessage =
              isExtranet && respData?.Error
                ? (respData?.TranslatedErrorMessage ?? translations?.NotInAssortment)
                : null;
            const unavailabilityMessage = repairBlocked
              ? translations?.NotInAssortment
              : (helperMessage ?? fallbackMessage);
            return (
              <div key={index}>
                <SparePartsKeys_ProductListItem
                  isKeysAndLocks={isKeysAndLocks}
                  translations={translations}
                  availability={respData?.Availability}
                  unavailabilityMessage={unavailabilityMessage}
                  deliveryDate={extranetHandler.getDeliverydate(sparePart)}
                  priceElement={getPriceElement(sparePart)}
                  getAddToCartButton={getAddToCartButton}
                  index={index}
                  itemData={getItemresponseData(sparePart.Id)}
                  sparePart={sparePart}
                />
              </div>
            );
          })}
        </div>
      )}
      {(!spareParts || spareParts.length === 0) && (
        <div className="text-center paragraph">{translations.NoSparepartsAvailable}</div>
      )}
    </div>
  );
};
export default SparePartsKeys_ProductList;
